import { useEffect, useState } from 'react';
import { executeService } from '../services/service';
import { useRbSystemAlert } from '@scmlatam/web-ui';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import type { Tag, UseToolbarProps } from '../types/hooks';
import type { Employee } from '../types/services';
import { PermisosEmpleado, usePermisoEmpleado } from '../../../../auth/permisosModulos';

const useToolbar = ({ activeStep }: UseToolbarProps) => {
    const canViewEmployees = usePermisoEmpleado('listado-empleados');
    const { showAlert } = useRbSystemAlert();
    const { t } = useTranslation('workers.table');

    const [tags, setTags] = useState<Tag[]>([]);
    const [tagSelected, setTagSelected] = useState<string[]>([]);
    const [searchTag, setSearchTag] = useState<string>('');

    const [employees, setEmployees] = useState<Employee[]>([]);

    const [filter, setFilter] = useState<string>('');

    const [loading, setLoading] = useState<boolean>(true);

    const search = async (start_date: string, end_date: string, tags_id__in: string) => {
        setLoading(true);

        let searchParams = new URLSearchParams(window.location.search);
        let newUrl = `${window.location.pathname}?${searchParams.toString()}`;

        const { employees } = await executeService.getEmployees({
            alert: { showAlert, t },
            params: {
                start_date: start_date,
                end_date: end_date,
                without_hiring: true,
                tags_id__in: tags_id__in,
            },
        });

        searchParams.set('tags', tags_id__in);
        newUrl = `${window.location.pathname}?${searchParams.toString()}`;
        window.history.replaceState(null, '', newUrl);

        setEmployees(employees);

        setLoading(false);
    };

    const reload = async () => {
        setLoading(true);

        let searchParams = new URLSearchParams(window.location.search);
        const start_date = searchParams.get('start_date') || '';
        const end_date = searchParams.get('end_date') || '';
        const tags_id__in = searchParams.get('tags') || '';

        const { employees } = await executeService.getEmployees({
            alert: { showAlert, t },
            params: {
                start_date: start_date,
                end_date: end_date,
                without_hiring: true,
                tags_id__in: tags_id__in,
            },
        });

        setEmployees(employees);

        setLoading(false);
    };

    const initializeToolbar = async () => {
        setLoading(true);

        let searchParams = new URLSearchParams(window.location.search);
        let newUrl = `${window.location.pathname}?${searchParams.toString()}`;

        const startDate = searchParams.get('start_date');
        const endDate = searchParams.get('end_date');
        const tagIds = searchParams.get('tags');

        const { tags } = await executeService.getTags({
            params: {},
            alert: { showAlert, t },
        });

        setTags(tags);

        if (dayjs(startDate).isValid() && dayjs(endDate).isValid() && tagIds) {
            setTagSelected(tagIds.split(','));
            await search(startDate || '', endDate || '', tagIds || '');

            searchParams.set('start_date', startDate || '');
            searchParams.set('end_date', endDate || '');
            searchParams.set('tags', tagIds || '');
        } else {
            const onlyfirstTag = tags.length > 0 ? [tags[0].value.toString()] : [];
            setTagSelected(onlyfirstTag);

            await search(dayjs().startOf('month').format('YYYY-MM-DD'), dayjs().endOf('month').format('YYYY-MM-DD'), onlyfirstTag.join(','));

            searchParams.set('start_date', dayjs().startOf('month').format('YYYY-MM-DD'));
            searchParams.set('end_date', dayjs().endOf('month').format('YYYY-MM-DD'));
            searchParams.set('tags', onlyfirstTag.join(','));
        }

        newUrl = `${window.location.pathname}?${searchParams.toString()}`;
        window.history.replaceState(null, '', newUrl);

        setLoading(false);
    };

    useEffect(() => {
        if (activeStep === 2 && canViewEmployees) {
            initializeToolbar();
        }
    }, [activeStep, canViewEmployees]);

    return {
        tags,
        setTags,
        tagSelected,
        setTagSelected,
        searchTag,
        setSearchTag,
        employees,
        setEmployees,
        filter,
        setFilter,
        loading,
        setLoading,
        search,
        reload,
        canViewEmployees,
        requiredEmployeePermissions: PermisosEmpleado['listado-empleados'],
    };
};

export { useToolbar };
